/**
 * Onboarding Wizard v1 UI Copy
 * Complete copy for the 6-step onboarding wizard
 * 
 * Usage: import { OnboardingCopy } from '@/copy/onboardingCopy';
 */

export const OnboardingCopy = {
  global: {
    header: "Commerce Index • Setup",
    stepLabel: (step, total) => `Step ${step} of ${total}`,
    exit: "Exit to Dashboard",
    skip: "Skip onboarding",
    footerHint: "You can come back anytime from Settings → Setup."
  },

  step0_welcome: {
    title: "Welcome to Commerce Index",
    subtitle: "Let's generate your Promise Health snapshot and show what's breaking — in under 10 minutes.",
    checklistTitle: "What you'll do",
    checklist: [
      "Check your connection status",
      "Generate your Promise Integrity Score",
      "Identify your top break driver",
      "Pick one playbook to implement this week",
      "Share a snapshot with your team",
      "(Optional) Connect Slack for approvals"
    ],
    primaryCta: "Continue",
    secondaryCta: "Do this later",
    note: "We focus on high-signal insights. No noise, no spam."
  },

  step1_connection: {
    title: "Connection Check",
    subtitle: "Confirm we can observe store signals and compute your snapshot.",

    states: {
      needs_auth: {
        title: "Sign in required",
        subtitle: "Your session has expired. Please sign in again to load your integration status.",
        body: "To protect your data, we require a fresh session to check store connection and event health.",
        primaryCta: "Sign in",
        secondaryCta: "Back",
        help: "If you keep seeing this after signing in, refresh the page and try again."
      },

      not_connected: {
        title: "Connect Shopify",
        subtitle: "Connect your store to unlock Promise Health, weekly digests, and benchmarks.",
        body: "Once connected, we'll observe orders and fulfillment signals and compute your Promise Integrity Score.",
        primaryCta: "Connect Shopify",
        secondaryCta: "Skip for now",
        help: "You can connect later in Settings → Integrations."
      },

      no_data: {
        title: "Connected — waiting for data",
        subtitle: "We're connected, but we haven't observed enough events yet to compute a full snapshot.",
        body: "This is normal right after install. You can still generate a partial snapshot now and it will improve as events flow in.",
        primaryCta: "Generate snapshot",
        secondaryCta: "I'll come back later",
        help: "Tip: the snapshot becomes meaningful after a few orders and fulfillment updates."
      },

      connected_healthy: {
        title: "Shopify connected ✅",
        subtitle: "You're ready. Let's generate your Promise Health snapshot.",
        stats: {
          ordersObserved: "Orders observed (7d)",
          lastEvent: "Last event",
          status: "Status",
          healthy: "Healthy"
        },
        primaryCta: "Generate snapshot",
        secondaryCta: "View integrations"
      },

      needs_attention: {
        title: "Shopify needs attention",
        subtitle: "We can't reliably read your store signals right now.",
        body: "This usually happens when a token expires or permissions change.",
        primaryCta: "Reconnect Shopify",
        secondaryCta: "Retry",
        help: "Once reconnected, your snapshot will update automatically."
      },

      error: {
        title: "Status temporarily unavailable",
        subtitle: "We couldn't verify your integration status.",
        body: "This may be a transient issue. Try again in a moment.",
        primaryCta: "Retry",
        secondaryCta: "Contact support"
      }
    }
  },

  step2_snapshot: {
    title: "Your Promise Health snapshot is ready",
    subtitle: "Here's what customers are experiencing right now — and what it's costing you.",
    labels: {
      score: "Promise Integrity Score",
      topDriver: "Top driver",
      benchmark: "Benchmark",
      leakage: "Estimated monthly leakage",
      savings: (pct) => `Potential savings (if breaks reduced by ${pct}%)`
    },
    benchmarkPending: "Benchmarking appears once 30+ similar merchants exist (privacy-safe thresholds).",
    primaryCta: "Show me what's breaking",
    secondaryCta: "Skip to sharing",
    note: "This snapshot gets more accurate as we observe more events."
  },

  step3_topDriver: {
    title: (driver) => `Biggest driver this week: ${driver}`,
    subtitle: "Fixing this is the fastest way to improve trust and reduce leakage.",
    metrics: {
      events: "Events",
      weightedImpact: "Weighted impact",
      ratePer100: "Rate per 100 promises"
    },
    insights: {
      cancellations: {
        title: "Why this matters",
        body: "Cancellations after confirmation are the highest-trust break. Reducing them often lowers support volume and improves repeat purchase."
      },
      backorders: {
        title: "Why this matters",
        body: "Backorders usually indicate weak availability confidence or oversell rules that are too aggressive. Treat this as a leading indicator."
      },
      late_partial: {
        title: "Why this matters",
        body: "Late/partial orders create repeat contacts and refunds. Multi-node sourcing and ship-complete constraints are common causes."
      },
      support: {
        title: "Why this matters",
        body: "Support cost often spikes when promise breaks aren't resolved early. Fixing the underlying driver reduces tickets."
      },
      default: {
        title: "Why this matters",
        body: "This driver is contributing to trust and margin leakage. Addressing it usually improves reliability quickly."
      }
    },
    primaryCta: "View recommended playbook",
    secondaryCta: "Skip"
  },

  step4_playbook: {
    title: "Next best action",
    subtitle: "Pick one change to implement this week. We'll track whether it improves your score.",
    cardTitle: (driver) => `Recommended actions for ${driver}`,
    buttons: {
      adopt: "Mark as adopted",
      task: "Create a task",
      skip: "Skip"
    },
    adoptedToast: "✅ Got it. We'll track your score and drivers next week to validate improvement.",
    help: "Start small. One improvement per week compounds fast."
  },

  step5_share: {
    title: "Share this with your team",
    subtitle: "A 30-second snapshot helps align ops, CX, and leadership.",
    actions: {
      copy: "Copy to clipboard",
      slack: "Send to Slack",
      badge: "Download badge"
    },
    slackNotConnected: "Send to Slack is available after you connect Slack.",
    primaryCta: "Finish onboarding",
    secondaryCta: "Connect Slack (optional)",
    note: "Weekly digests are delta-gated — we only send when something changes."
  },

  step6_slack: {
    connected: {
      title: "Slack connected ✅",
      subtitle: "Approvals and weekly digests can post to your default channel.",
      body: "You can route exception approvals into Slack and manage them from Approval Queue.",
      primaryCta: "Finish",
      secondaryCta: "Open Slack settings"
    },
    notConnected: {
      title: "Want approvals to run faster?",
      subtitle: "Connect Slack to route exception approvals into channel and build precedent.",
      bullets: [
        "Approve/reject exceptions in Slack",
        "Approval Queue (bulk, snooze, reminders)",
        "Precedent Library (mark/cite/apply)"
      ],
      primaryCta: "Connect Slack",
      secondaryCta: "Do this later",
      help: "Slack is optional — Promise Health works without it."
    }
  },

  completion: {
    title: "You're all set ✅",
    subtitle: "Your Promise Health dashboard will update as events flow in.",
    buttons: {
      promiseHealth: "Open Promise Health",
      exceptionRadar: "Open Exception Radar",
      slackSettings: "Open Slack settings"
    },
    note: "Your weekly digest posts only when meaningful changes occur — no spam."
  }
};

/**
 * Maps integration status API response to onboarding wizard state
 * 
 * States: needs_auth, not_connected, no_data, connected_healthy, needs_attention, error
 * 
 * @param {number} resStatus - HTTP response status
 * @param {object} data - Response JSON body
 * @param {string} contentType - Response content-type header
 * @returns {string} - Mapped onboarding state
 */
export function mapIntegrationStatusToOnboardingState(resStatus, data, contentType) {
  // 1) Hard auth failure
  if (resStatus === 401) return "needs_auth";
  if (contentType?.includes("text/html")) return "needs_auth";
  if (data?.state === "needs_auth") return "needs_auth";

  // 2) Backend error
  if (!data || resStatus >= 500) return "error";
  if (data.state === "error") return "error";

  // 3) Shopify connection
  const shopify = data.shopify || data.integrations?.shopify;
  const connected = shopify?.connected === true;

  if (!connected) return "not_connected";

  // 4) Needs attention
  if (shopify?.needs_reauth === true || data.state === "needs_attention") return "needs_attention";

  // 5) No data
  const events7d = data.events?.total_views_30d ?? data.events?.count_7d ?? data.events?.count7d ?? 0;
  const lastEvent = data.events?.last_view_at ?? data.events?.last_event_at ?? null;
  if (events7d === 0 && !lastEvent) return "no_data";

  // 6) Healthy
  return "connected_healthy";
}

/**
 * Analytics events for onboarding tracking
 */
export const OnboardingEvents = {
  STARTED: 'onboarding_started',
  INTEGRATION_STATE_SEEN: 'integration_state_seen',
  SNAPSHOT_GENERATED: 'snapshot_generated',
  PLAYBOOK_ADOPTED: 'playbook_adopted',
  REPORT_SHARED_COPY: 'report_shared_copy',
  REPORT_SHARED_SLACK: 'report_shared_slack',
  SLACK_CONNECT_CLICKED: 'slack_connect_clicked',
  COMPLETED: 'onboarding_completed'
};
